<template>
  <div class="py-8">
    <div class="w-full max-w-lg mx-auto">
      <StepHeader :step="step" :total="totalSteps" :submitted="submitted" />
      <component
        :is="currentStepComponent"
        v-bind="stepProps"
        v-model:modelValue="formStore.answers"
        @next="nextStep"
        @back="prevStep"
        @complete="completeForm" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

// Define emits
const emit = defineEmits<{
  complete: [];
}>();

// Step components
import Start from "@/components/forms/SmartLeadForm/Start.vue";
import Situation from "@/components/forms/SmartLeadForm/Situation.vue";
import Goal from "@/components/forms/SmartLeadForm/Goal.vue";
import Budget from "@/components/forms/SmartLeadForm/Budget.vue";
import Involvement from "@/components/forms/SmartLeadForm/Involvement.vue";
import Timeline from "@/components/forms/SmartLeadForm/Timeline.vue";
import PlanReady from "@/components/forms/SmartLeadForm/PlanReady.vue";
import Contact from "@/components/forms/SmartLeadForm/Contact.vue";
import GDPR from "@/components/forms/SmartLeadForm/GDPR.vue";
import ThankYou from "@/components/forms/SmartLeadForm/ThankYou.vue";
import Goodbye from "@/components/forms/SmartLeadForm/Goodbye.vue";

import StepHeader from "@/components/forms/SmartLeadForm/StepHeader.vue";
import { useFormStore } from "@/features/blueprint/stores/useFormStore";

const step = ref(0);
const submitted = ref(false);
const formStore = useFormStore();

const steps = [Start, Situation, Goal, Budget, Involvement, Timeline, PlanReady, Contact, GDPR, ThankYou, Goodbye];

const totalSteps = 5;

const currentStepComponent = computed(() => steps[step.value]);

const stepProps = computed(() => ({
  modelValue: formStore.answers,
  submitted: submitted.value,
}));

function nextStep() {
  if (step.value < steps.length - 1) step.value++;
}

function prevStep() {
  if (step.value > 0) step.value--;
}

async function completeForm() {
  const d = formStore.answers;

  // Map answers to product recommendations
  const businessStage = d.situation;
  const primaryGoal = d.goal;
  const investmentLevel = d.budget;

  // Determine product category based on answers
  let productCategory = "buildLaunch"; // default

  if (primaryGoal === "getClients" || businessStage === "haveWebsite") {
    productCategory = "growthOptimize";
  } else if (primaryGoal === "fixMaintain" || businessStage === "needMaintenance") {
    productCategory = "techConcierge";
  }

  // Set product recommendations based on category and investment level
  formStore.answers.recommendedCategory = productCategory;
  formStore.answers.recommendedProducts = getRecommendedProducts(
    productCategory,
    typeof investmentLevel === "string" ? investmentLevel : "starter",
    d
  );

  // Save to localStorage and wait a moment to ensure it's persisted
  formStore.saveToLocalStorage();

  // Use nextTick to ensure the save is complete before navigating
  await new Promise(resolve => setTimeout(resolve, 100));

  submitted.value = true;

  // Emit complete event instead of direct navigation to let parent handle it
  emit("complete");
}

function getRecommendedProducts(category: string, investmentLevel: string, answers: any): string[] {
  let products: string[] = [];

  if (investmentLevel === "starter") {
    products = [
      "webHosting_total", // Complete Website Pakket (€30/maand)
      "domain_managed", // Beheerde Domein Service (€15/jaar)
      "branding_mini", // Mini Merk Refresh (€490 eenmalig)
      "techConcierge_extra_5h", // Tech Concierge 5u Pakket (€250 eenmalig)
    ];
  } else if (investmentLevel === "professional") {
    products = [
      "webHosting_pro", // Complete Website Pakket Uitgebreid (€50/maand)
      "growthCall_monthly", // Groei Analytics Tracker Pro (€89/maand)
      "branding_full", // Complete Merk Evolutie (€790 eenmalig)
      "seo_booster", // SEO Kansen Versterker (€890 eenmalig)
    ];
  } else if (investmentLevel === "premium") {
    products = [
      "webHosting_pro", // Complete Website Pakket Uitgebreid (€50/maand)
      "growthCall_monthly", // Groei Analytics Tracker Pro (€89/maand)
      "humanContent_business", // Autoriteit Content Accelerator (€450/maand)
      "branding_premium", // Signature Merk Ervaring (€1490 eenmalig)
      "cro_booster", // CRO Conversie Accelerator (€1290 eenmalig)
      "seo_booster", // SEO Kansen Versterker (€890 eenmalig)
      "content_strategy", // Content Strategie Intensief (€2490 eenmalig)
    ];
  }

  // Then apply situation-specific refinements
  const businessStage = answers.situation;
  const primaryGoal = answers.goal;

  // "Ik heb nog geen website"
  if (businessStage === "starting") {
    if (investmentLevel === "starter") {
      // Basis: Website Pakket + Domein + Merk Refresh (already in starter budget)
      // Ensure domain is included
      if (!products.includes("domain_managed")) {
        products.push("domain_managed");
      }
    } else {
      // Premium: Website Pakket Pro + Complete Merk Evolutie + SEO Booster (already in professional/premium budgets)
      // Ensure these are included
      if (!products.includes("domain_managed")) {
        products.push("domain_managed");
      }
    }
  }

  // "Mijn website is verouderd"
  if (businessStage === "haveWebsite") {
    // Focus op: CRO Accelerator + Merk Refresh + Analytics Tracker
    // Remove hosting packages since they have existing website
    products = products.filter(p => !p.startsWith("webHosting_"));
    products = products.filter(p => p !== "domain_managed"); // Don't need new domain

    // Add specific packages for outdated websites
    if (!products.includes("cro_booster")) {
      products.push("cro_booster");
    }
    if (
      !products.includes("branding_mini") &&
      !products.includes("branding_full") &&
      !products.includes("branding_premium")
    ) {
      products.push("branding_mini");
    }
    if (!products.includes("growthCall_monthly")) {
      products.push("growthCall_monthly");
    }
  }

  // "Ik wil meer leads"
  if (primaryGoal === "getClients") {
    // Focus op: SEO Booster + Content Accelerator + Analytics Tracker
    if (!products.includes("seo_booster")) {
      products.push("seo_booster");
    }
    if (!products.includes("humanContent_business") && investmentLevel !== "starter") {
      products.push("humanContent_business");
    }
    if (!products.includes("growthCall_monthly")) {
      products.push("growthCall_monthly");
    }
  }

  // "Ik heb weinig tijd voor marketing"
  if (answers.involvement === "handsOff") {
    // Focus op: Autoriteit Content Accelerator + Analytics Tracker + Tech Concierge extra uren
    if (!products.includes("humanContent_business") && investmentLevel !== "starter") {
      products.push("humanContent_business");
    }
    if (!products.includes("growthCall_monthly")) {
      products.push("growthCall_monthly");
    }
    if (!products.includes("techConcierge_extra_5h") && !products.includes("techConcierge_5h")) {
      if (investmentLevel === "premium") {
        products.push("techConcierge_5h");
      } else {
        products.push("techConcierge_extra_5h");
      }
    }
  }

  // E-commerce/Webshop focus (inferred from business needs)
  if (primaryGoal === "getClients" && businessStage === "haveWebsite") {
    // Essentieel: CRO Accelerator + Analytics Tracker
    if (!products.includes("cro_booster")) {
      products.push("cro_booster");
    }
    if (!products.includes("growthCall_monthly")) {
      products.push("growthCall_monthly");
    }
    // Extra: SEO Booster voor organisch traffic
    if (!products.includes("seo_booster")) {
      products.push("seo_booster");
    }
  }

  // B2B Service bedrijf (inferred from brand focus and lead generation)
  if (primaryGoal === "buildWebsite" && businessStage === "starting") {
    // Focus op: Complete Merk Evolutie + Autoriteit Content + SEO Booster
    if (
      !products.includes("branding_full") &&
      !products.includes("branding_premium") &&
      investmentLevel !== "starter"
    ) {
      if (investmentLevel === "premium") {
        products.push("branding_premium");
      } else {
        products.push("branding_full");
      }
    }
    if (!products.includes("humanContent_business") && investmentLevel !== "starter") {
      products.push("humanContent_business");
    }
    if (!products.includes("seo_booster")) {
      products.push("seo_booster");
    }
  }

  // Maintenance and fixes focus
  if (primaryGoal === "fixMaintain" || businessStage === "needMaintenance") {
    // Focus on tech concierge services
    if (!products.includes("techConcierge_free")) {
      products.push("techConcierge_free");
    }
    if (
      investmentLevel !== "starter" &&
      !products.includes("techConcierge_extra_5h") &&
      !products.includes("techConcierge_5h")
    ) {
      if (investmentLevel === "premium") {
        products.push("techConcierge_5h");
      } else {
        products.push("techConcierge_extra_5h");
      }
    }
  }

  // Urgency-based additions
  if (answers.timeline === "now" && investmentLevel !== "starter") {
    // Add extra support for urgent projects
    if (!products.includes("techConcierge_extra_5h") && !products.includes("techConcierge_5h")) {
      products.push("techConcierge_extra_5h");
    }
  }

  return [...new Set(products)]; // Remove duplicates
}
</script>
